/**
 * Parent settings: BYOK provider config, Stripe billing entry points, and
 * an account panel with identity + sign-out.
 *
 * The BYOK API key is envelope-encrypted with the family key on-device and
 * stored in IndexedDB — it is never POSTed to the control plane in plaintext
 * (see `data/byok.ts`). Billing routes redirect to Stripe-hosted pages.
 */

import { useEffect, useState } from 'preact/hooks';
import { useLocation } from 'preact-iso';
import { fetchLatest } from '../data/config';
import {
  BYOK_VAULT_KEY,
  endpointRequestUrl,
  fetchByok,
  fetchByokTest,
  requestByokTest,
  saveByok,
  type ByokTestResult,
} from '../data/byok';
import { ApiError } from '../state/api';
import * as vault from '../e2ee/vault';
import {
  startCheckout,
  openPortal,
  getBillingStatus,
  BillingNotAvailableError,
  type BillingStatus,
  type Cadence,
} from '../data/billing';
import {
  getNotificationPrefs,
  saveNotificationPrefs,
  sendTestNotification,
  verifySmsStart,
  verifySmsConfirm,
} from '../data/notifications';
import { markOnboardingTestSent } from '../components/OnboardingChecklist';
import { authSession, logout, clearAuthSession } from '../state/session';
import { Button } from '../components/Button';
import { Card } from '../components/Card';
import { Input } from '../components/Input';

type Provider = 'anthropic' | 'openai' | 'google' | 'openai_compatible';

function isProvider(v: string): v is Provider {
  return (
    v === 'anthropic' ||
    v === 'openai' ||
    v === 'google' ||
    v === 'openai_compatible'
  );
}

/**
 * Curated per-provider model lists. Each entry pairs the exact API model ID
 * (the string the endpoint sends to the provider) with a human-facing label.
 * First entry in each list is the sensible default. OpenAI and Anthropic
 * IDs verified on 2026-09-23; Google IDs verified on 2026-09-06.
 * https://developers.openai.com/api/docs/models
 * https://platform.claude.com/docs/en/models/overview
 *
 * The Google `-latest` entries are Google-maintained aliases
 * (`gemini-flash-latest`, `gemini-pro-latest`) that hot-swap to the newest
 * release of their tier — including preview builds — so new model versions
 * arrive without a Backstop release. Google announces breaking alias
 * changes two weeks ahead by email.
 *
 * `openai_compatible` intentionally has no list — self-hosted deployments
 * (Ollama, vLLM, LM Studio) each expose their own model names, so we fall
 * through to a free-form text input for that provider.
 */
const MODELS: Record<
  Exclude<Provider, 'openai_compatible'>,
  { id: string; label: string }[]
> = {
  anthropic: [
    { id: 'claude-sonnet-5', label: 'Claude Sonnet 5 — balanced (default)' },
    { id: 'claude-opus-5-5', label: 'Claude Opus 5.5 — advanced' },
    { id: 'claude-fable-5-1', label: 'Claude Fable 5.1 — top capability' },
    {
      id: 'claude-haiku-4-5-20251001',
      label: 'Claude Haiku 4.5 — fast & cheap',
    },
  ],
  openai: [
    { id: 'gpt-6-luna', label: 'GPT-6 Luna — fast & cheap (default)' },
    { id: 'gpt-6-sol', label: 'GPT-6 Sol — balanced' },
    { id: 'gpt-6-astra', label: 'GPT-6 Astra — top capability' },
  ],
  google: [
    { id: 'gemini-3.7-flash', label: 'Gemini 3.7 Flash — balanced (default)' },
    {
      id: 'gemini-flash-latest',
      label: 'Gemini Flash (latest) — auto-updates to the newest Flash',
    },
    {
      id: 'gemini-3.1-pro-preview',
      label: 'Gemini 3.1 Pro (preview) — top capability',
    },
    {
      id: 'gemini-pro-latest',
      label: 'Gemini Pro (latest) — auto-updates to the newest Pro',
    },
    { id: 'gemini-3.5-flash-lite', label: 'Gemini 3.5 Flash-Lite — cheapest' },
  ],
};

/** First option in each provider's list. */
function defaultModelFor(provider: Provider): string {
  if (provider === 'openai_compatible') return 'llama3.2:latest';
  return MODELS[provider][0]?.id ?? '';
}

function ByokPanel() {
  const [provider, setProvider] = useState<Provider>('anthropic');
  const [model, setModel] = useState(defaultModelFor('anthropic'));
  const [endpoint, setEndpoint] = useState('');
  // Append /chat/completions, or use the endpoint URL verbatim? Settings
  // saved under the old "URL ends with the path" heuristic default to
  // verbatim so their intent survives.
  const [appendPath, setAppendPath] = useState(true);

  // The provider lives in the byok row beside its key, not in the config
  // document, so that is where the screen reads it from.
  //
  // The fields are set here rather than seeded through `useState`
  // initializers: those run once, on first render, and would snapshot the
  // placeholder above forever while the real settings arrived a tick later.
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const saved = await fetchByok().catch(() => null);
      if (saved === null || cancelled) return;
      const p: Provider = isProvider(saved.provider)
        ? saved.provider
        : 'anthropic';
      setProvider(p);
      setModel(saved.model.length > 0 ? saved.model : defaultModelFor(p));
      setEndpoint(saved.endpoint ?? '');
      setAppendPath(
        saved.endpoint_append_path ??
          !/\/chat\/completions\/?$/i.test(saved.endpoint ?? ''),
      );
    })();
    return () => {
      cancelled = true;
    };
  }, []);
  const [apiKey, setApiKey] = useState('');
  const [status, setStatus] = useState<'idle' | 'saving' | 'ok' | 'error'>(
    'idle',
  );
  const [error, setError] = useState<string | null>(null);
  // Result of the optional pre-save connectivity check, run on the device.
  const [test, setTest] = useState<'idle' | 'running' | 'ok' | 'error'>('idle');
  const [testDetail, setTestDetail] = useState<string | null>(null);
  // Which device answered the trigger, for the waiting and result copy.
  const [testDevice, setTestDevice] = useState<string | null>(null);
  // Presence-only signal — we can't decrypt the ciphertext without the
  // family key + AAD dance, and we intentionally never surface the
  // plaintext, but we CAN tell whether a ciphertext is on file so the
  // panel doesn't look empty after a reload.
  const [keyConfigured, setKeyConfigured] = useState(false);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const ct = await vault.get(BYOK_VAULT_KEY);
      if (!cancelled) setKeyConfigured(ct !== null);
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  async function onSave(): Promise<void> {
    setStatus('saving');
    setError(null);
    try {
      await saveByok(
        {
          provider,
          model,
          endpoint: provider === 'openai_compatible' ? endpoint : null,
          ...(provider === 'openai_compatible'
            ? { endpoint_append_path: appendPath }
            : {}),
        },
        apiKey,
      );
      setApiKey('');
      setKeyConfigured(true);
      setStatus('ok');
    } catch (e) {
      setStatus('error');
      const msg = e instanceof Error ? e.message : String(e);
      setError(msg);
      console.error('saveByok failed:', msg);
    }
  }

  /** Resolve after `ms`. Executor form because the tsconfig lib predates
   * `Promise.withResolvers` (ES2024) — change both together if the lib moves. */
  function sleep(ms: number): Promise<undefined> {
    return new Promise<undefined>((resolve) => {
      setTimeout(resolve, ms);
    });
  }

  /**
   * How long to keep polling before giving up on the device.
   *
   * Deliberately longer than the server's 180s test lifetime, so the
   * parent's poll crosses the threshold, triggers the server's lazy settle,
   * and gets a real `timeout` status — rather than giving up first and
   * leaving the candidate key parked.
   */
  const TEST_TIMEOUT_MS = 210_000;
  /** Gap between polls. The device answers on its next 60s heartbeat. */
  const TEST_POLL_MS = 3_000;

  async function onTest(): Promise<void> {
    setTest('running');
    setTestDetail(null);
    setTestDevice(null);
    try {
      const { test_id, device_label } = await requestByokTest(
        {
          provider,
          model,
          endpoint: provider === 'openai_compatible' ? endpoint : null,
          ...(provider === 'openai_compatible'
            ? { endpoint_append_path: appendPath }
            : {}),
        },
        apiKey,
      );
      setTestDevice(device_label);

      const deadline = Date.now() + TEST_TIMEOUT_MS;
      for (;;) {
        await sleep(TEST_POLL_MS);
        let out: ByokTestResult;
        try {
          out = await fetchByokTest(test_id);
        } catch (e) {
          // A 404 on the poll means this test was superseded: a second
          // trigger replaced its row id, so this one is gone. Terminal for
          // this poll, not a hang — tell the parent what actually happened.
          if (e instanceof ApiError && e.status === 404) {
            setTest('error');
            setTestDetail(
              'This test was replaced by a newer one, probably from ' +
                'another parent. Run it again to test the latest settings.',
            );
            return;
          }
          throw e;
        }
        if (out.status === 'ok') {
          setTest('ok');
          setTestDetail(out.reply ?? 'ok');
          return;
        }
        if (out.status === 'failed') {
          setTest('error');
          setTestDetail(out.error ?? 'the provider rejected the request');
          return;
        }
        if (out.status === 'timeout' || Date.now() > deadline) {
          setTest('error');
          setTestDetail(
            `${device_label} did not answer in time. It may have gone offline.`,
          );
          return;
        }
      }
    } catch (e) {
      setTest('error');
      // The 409s are the two states the parent can actually act on, so each
      // gets its own sentence rather than a raw problem-detail string.
      if (e instanceof ApiError && e.detail.includes('no_device_online')) {
        setTestDetail(
          'No device is online to run the test. The test runs on the ' +
            'monitored device, so one needs to be switched on.',
        );
        return;
      }
      if (e instanceof ApiError && e.detail.includes('device_needs_update')) {
        setTestDetail(
          'This device is running an older version of Backstop that cannot ' +
            'run tests yet. It updates itself within a few hours.',
        );
        return;
      }
      setTestDetail(e instanceof Error ? e.message : String(e));
    }
  }

  return (
    <section class="space-y-3">
      <h2 class="text-2xl font-semibold">LLM Provider</h2>
      <Card class="space-y-3">
        <label class="block text-sm">
          <span>Provider</span>
          <select
            aria-label="Provider"
            value={provider}
            onChange={(e) => {
              const v = (e.target as HTMLSelectElement).value;
              if (isProvider(v)) {
                setProvider(v);
                // Reset model to that provider's default whenever the
                // provider changes, so we never save an Anthropic model ID
                // under an OpenAI provider selection.
                setModel(defaultModelFor(v));
              }
            }}
            class="mt-1 h-10 w-full rounded border border-surface-muted/40 bg-surface px-3 text-base"
          >
            <option value="anthropic">Anthropic (Claude)</option>
            <option value="openai">OpenAI</option>
            <option value="google">Google (Gemini)</option>
            <option value="openai_compatible">
              OpenAI-compatible (Ollama, self-hosted)
            </option>
          </select>
        </label>

        <label class="block text-sm">
          <span>Model</span>
          {provider === 'openai_compatible' ? (
            // Self-hosted stacks each expose their own model names; a
            // dropdown would just be wrong. Free-form input, seeded with
            // a common Ollama tag as an example.
            <Input
              aria-label="Model"
              class="mt-1"
              placeholder="llama3.2:latest"
              value={model}
              onInput={(e) => {
                setModel((e.target as HTMLInputElement).value);
              }}
            />
          ) : (
            <select
              aria-label="Model"
              value={model}
              onChange={(e) => {
                setModel((e.target as HTMLSelectElement).value);
              }}
              class="mt-1 h-10 w-full rounded border border-surface-muted/40 bg-surface px-3 text-base"
            >
              {MODELS[provider].map((m) => (
                <option key={m.id} value={m.id}>
                  {m.label}
                </option>
              ))}
              {MODELS[provider].some((m) => m.id === model) ? null : (
                // Saved model ID no longer in the curated list (e.g.
                // picked from an older release's options): keep showing
                // it so what the dropdown displays is what saves.
                <option value={model}>{model} — saved</option>
              )}
            </select>
          )}
        </label>

        {provider === 'openai_compatible' ? (
          <label class="block text-sm">
            <span>Endpoint URL</span>
            <Input
              aria-label="Endpoint URL"
              class="mt-1"
              placeholder="http://localhost:11434/v1"
              value={endpoint}
              onInput={(e) => {
                setEndpoint((e.target as HTMLInputElement).value);
              }}
            />
            <label class="mt-1 flex items-center gap-2 text-xs">
              <input
                type="checkbox"
                checked={appendPath}
                onChange={(e) => {
                  setAppendPath((e.target as HTMLInputElement).checked);
                }}
              />
              <span>
                Append <code>/chat/completions</code> to the URL (uncheck if the
                URL is already the complete request target)
              </span>
            </label>
            {endpoint.trim().length > 0 ? (
              <span class="mt-1 block break-all text-xs">
                <span class="text-surface-muted">Will POST to </span>
                <code class="break-all rounded bg-surface-muted/20 px-1 py-0.5 font-mono">
                  {endpointRequestUrl(endpoint, appendPath)}
                </code>
              </span>
            ) : null}
          </label>
        ) : null}

        <label class="block text-sm">
          <span class="flex items-center gap-2">
            <span>API key</span>
            {keyConfigured && (
              <span
                title="A key is saved for this account. Enter a new one to replace it; leave blank to keep the existing one."
                class="font-mono text-[10px] tracking-[0.14em] uppercase text-[color:var(--color-signal)] border border-[color:var(--color-signal)]/40 rounded px-1.5 py-0.5"
              >
                Configured
              </span>
            )}
          </span>
          <Input
            aria-label="API key"
            class="mt-1"
            type="password"
            placeholder={
              keyConfigured ? 'Leave blank to keep the existing key' : ''
            }
            value={apiKey}
            onInput={(e) => {
              setApiKey((e.target as HTMLInputElement).value);
            }}
          />
          <span class="mt-1 block text-xs text-surface-muted">
            Encrypted on this device with your family key. Never seen by our
            servers in plaintext.
          </span>
        </label>

        <div class="flex items-center gap-3">
          <Button onClick={() => void onSave()} disabled={status === 'saving'}>
            {status === 'saving' ? 'Saving…' : 'Save BYOK'}
          </Button>
          <Button
            variant="secondary"
            onClick={() => void onTest()}
            disabled={test === 'running'}
          >
            {test === 'running'
              ? testDevice === null
                ? 'Asking your device…'
                : `Asking ${testDevice}… this can take a few minutes`
              : 'Send test prompt'}
          </Button>
          {status === 'ok' ? (
            <span class="text-sm text-emerald-600">Saved.</span>
          ) : null}
          {status === 'error' && error !== null ? (
            <span class="text-sm text-red-600">Save failed: {error}</span>
          ) : null}
        </div>
        {test === 'ok' && testDetail !== null ? (
          <div>
            <p class="text-sm text-emerald-600">
              Test passed — {testDevice ?? 'your device'} reached the provider.
            </p>
            <p class="mt-1 text-xs text-surface-muted">
              Provider reply:{' '}
              <code class="break-all rounded bg-surface-muted/20 px-1 py-0.5 font-mono">
                {testDetail.length > 240
                  ? `${testDetail.slice(0, 240)}…`
                  : testDetail}
              </code>
            </p>
          </div>
        ) : null}
        {test === 'error' && testDetail !== null ? (
          <p class="text-sm text-red-600">Test failed: {testDetail}</p>
        ) : null}
        <p class="text-xs text-surface-muted">
          Your child's device runs this test with the settings above, on the
          same path it uses for real screenshots. Nothing is saved until you
          press Save BYOK — so a failed test changes nothing.
        </p>
      </Card>
    </section>
  );
}

/**
 * If the current URL carries `?cadence=annual` (or `monthly`), lift it as the
 * initial toggle state. The marketing pricing page links to signup with this
 * search param so a parent who clicked "Annual $89" on the website lands
 * pre-selected here.
 */
function initialCadenceFromUrl(): Cadence {
  try {
    const p = new URLSearchParams(window.location.search).get('cadence');
    if (p === 'annual' || p === 'monthly') return p;
  } catch {
    // No window / no URL — SSR or a very hostile environment. Fall through.
  }
  return 'monthly';
}

const MS_PER_DAY = 24 * 60 * 60 * 1000;

/**
 * Render the parent-facing billing state as a coloured pill + supporting
 * detail line. Trial-with-days-left is the noisy case; active-with-renewal-
 * date is the boring-but-important case; past-due is loud on purpose.
 */
function BillingStatusSummary({ status }: { status: BillingStatus }) {
  const now = Date.now();

  function fmtDate(iso: string): string {
    const d = new Date(iso);
    return d.toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  }

  let pillLabel = '';
  let pillClass = '';
  let detail = '';

  switch (status.subscription_status) {
    case 'active': {
      pillLabel = 'Subscribed';
      pillClass = 'bg-emerald-100 text-emerald-800 border-emerald-300';
      detail =
        status.current_period_end !== null
          ? `Renews ${fmtDate(status.current_period_end)}.`
          : 'Renews automatically.';
      break;
    }
    case 'past_due': {
      pillLabel = 'Payment problem';
      pillClass = 'bg-red-100 text-red-800 border-red-300';
      detail =
        'Your last payment was declined. Update your card via Manage subscription to keep capture running.';
      break;
    }
    case 'canceled':
    case 'unpaid': {
      pillLabel = 'Not subscribed';
      pillClass = 'bg-surface-muted/20 text-surface-fg border-surface-muted/40';
      detail = 'Subscribe to resume capture on your endpoints.';
      break;
    }
    case 'trialing': {
      const trialEndMs = Date.parse(status.trial_ends_at);
      const msLeft = Number.isNaN(trialEndMs) ? 0 : trialEndMs - now;
      const daysLeft = Math.max(0, Math.ceil(msLeft / MS_PER_DAY));
      if (!status.entitled || msLeft <= 0) {
        pillLabel = 'Trial ended';
        pillClass = 'bg-red-100 text-red-800 border-red-300';
        detail =
          'Your 14-day trial has ended. Subscribe to resume capture on your endpoints.';
      } else if (daysLeft <= 3) {
        pillLabel = `Trial ends in ${daysLeft.toString()} day${daysLeft === 1 ? '' : 's'}`;
        pillClass = 'bg-amber-100 text-amber-900 border-amber-300';
        detail = `Trial ends ${fmtDate(status.trial_ends_at)}. Subscribe now to avoid interruption.`;
      } else if (daysLeft <= 7) {
        pillLabel = `Trial: ${daysLeft.toString()} days left`;
        pillClass = 'bg-amber-100 text-amber-900 border-amber-300';
        detail = `Trial ends ${fmtDate(status.trial_ends_at)}.`;
      } else {
        pillLabel = `Trial: ${daysLeft.toString()} days left`;
        pillClass = 'bg-emerald-100 text-emerald-800 border-emerald-300';
        detail = `Trial ends ${fmtDate(status.trial_ends_at)}.`;
      }
      break;
    }
    default: {
      pillLabel = status.subscription_status;
      pillClass = 'bg-surface-muted/20 text-surface-fg border-surface-muted/40';
      break;
    }
  }

  return (
    <div class="flex flex-col gap-1">
      <div class="flex items-center gap-2">
        <span
          class={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-medium ${pillClass}`}
        >
          {pillLabel}
        </span>
      </div>
      {detail !== '' && <p class="text-sm text-surface-muted">{detail}</p>}
    </div>
  );
}

function BillingPanel() {
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState<'checkout' | 'portal' | null>(null);
  const [cadence, setCadence] = useState<Cadence>(initialCadenceFromUrl());
  const [status, setStatus] = useState<BillingStatus | null>(null);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const s = await getBillingStatus();
        if (!cancelled) setStatus(s);
      } catch {
        // Billing not configured or transient — leave status null and
        // the panel falls back to just the action buttons.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  async function run(
    fn: () => Promise<void>,
    kind: 'checkout' | 'portal',
  ): Promise<void> {
    setNotice(null);
    setBusy(kind);
    try {
      await fn();
    } catch (e) {
      if (e instanceof BillingNotAvailableError) {
        setNotice('Billing coming soon.');
      } else {
        setNotice(e instanceof Error ? e.message : String(e));
      }
    } finally {
      setBusy(null);
    }
  }

  const cadenceOptions: { value: Cadence; label: string }[] = [
    { value: 'monthly', label: 'Monthly $9' },
    { value: 'annual', label: 'Annual $89' },
  ];

  return (
    <section class="space-y-3">
      <h2 class="text-2xl font-semibold">Billing</h2>
      <Card class="space-y-3">
        {status !== null && <BillingStatusSummary status={status} />}
        <p class="text-sm text-surface-muted">
          Subscription is managed through Stripe. Clicking below opens the
          Stripe-hosted flow.
        </p>
        <div
          role="radiogroup"
          aria-label="Billing cadence"
          class="inline-flex rounded border border-surface-muted/40 p-0.5"
        >
          {cadenceOptions.map((opt) => {
            const selected = cadence === opt.value;
            return (
              <button
                key={opt.value}
                type="button"
                role="radio"
                aria-checked={selected}
                onClick={() => {
                  setCadence(opt.value);
                }}
                class={
                  'rounded px-3 py-1.5 text-sm font-medium transition ' +
                  (selected
                    ? 'bg-brand-500 text-white'
                    : 'text-surface-fg hover:bg-surface-muted/10')
                }
              >
                {opt.label}
              </button>
            );
          })}
        </div>
        <div class="flex flex-wrap gap-2">
          <Button
            onClick={() => void run(() => startCheckout(cadence), 'checkout')}
            disabled={busy !== null}
          >
            {busy === 'checkout' ? 'Loading…' : 'Subscribe'}
          </Button>
          <Button
            variant="secondary"
            onClick={() => void run(openPortal, 'portal')}
            disabled={busy !== null}
          >
            {busy === 'portal' ? 'Loading…' : 'Manage subscription'}
          </Button>
        </div>
        {notice !== null ? (
          <p class="text-sm text-surface-muted">{notice}</p>
        ) : null}
      </Card>
    </section>
  );
}

/**
 * Notification channel prefs — which of SMS / email / browser push should
 * receive alerts, plus the destinations for each. Also the "test" button
 * that fires a canned message down the enabled channels so a parent
 * can prove notifications actually reach them before their kid does
 * anything concerning.
 *
 * Push subscription: a browser Push API subscription obtained via
 * `PushManager.subscribe`. The full subscription JSON (endpoint + p256dh
 * key + auth secret) is sent to the CP so the web-push provider can
 * encrypt directly to it.
 */
/**
 * True if `s` is `+1` followed by exactly 10 digits. Mirrors the
 * server's `is_us_e164` — clients that skip this get a 400 from the
 * server rather than a silent bad-Twilio-request later on.
 */
function isUsE164(s: string): boolean {
  return /^\+1\d{10}$/.test(s);
}

function NotificationsPanel() {
  const [smsNumber, setSmsNumber] = useState('');
  const [email, setEmail] = useState('');
  const [pushSub, setPushSub] = useState<unknown | null>(null);
  const [smsOn, setSmsOn] = useState(false);
  const [emailOn, setEmailOn] = useState(false);
  const [pushOn, setPushOn] = useState(false);
  // The specific SMS number the server currently considers verified,
  // or null if no verification stands. Compare against `smsNumber` at
  // render time: if the parent has edited the input to a different
  // value, the VERIFIED badge disappears and the "Send code" button
  // reappears — otherwise verification silently trails behind stale
  // input.
  const [serverVerifiedNumber, setServerVerifiedNumber] = useState<
    string | null
  >(null);
  // ISO expiry of an outstanding verification code; null if no code is
  // outstanding. Presence drives the "verify in flight" panel state.
  const [smsExpiresAt, setSmsExpiresAt] = useState<string | null>(null);
  const [smsCode, setSmsCode] = useState('');
  const [verifyStatus, setVerifyStatus] = useState<
    'idle' | 'starting' | 'confirming' | 'error'
  >('idle');
  const [verifyError, setVerifyError] = useState<string | null>(null);
  const [status, setStatus] = useState<
    'loading' | 'idle' | 'saving' | 'ok' | 'error'
  >('loading');
  const [testStatus, setTestStatus] = useState<
    'idle' | 'sending' | 'ok' | 'error'
  >('idle');
  const [testResult, setTestResult] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const p = await getNotificationPrefs();
        if (cancelled) return;
        setSmsNumber(p.sms_number ?? '');
        setEmail(p.email_address ?? '');
        setPushSub(p.push_subscription);
        setSmsOn(p.sms_enabled);
        setEmailOn(p.email_enabled);
        setPushOn(p.push_enabled);
        setServerVerifiedNumber(
          p.sms_verified && p.sms_number !== null ? p.sms_number : null,
        );
        setSmsExpiresAt(p.sms_verification_expires_at);
        setStatus('idle');
      } catch (e) {
        if (cancelled) return;
        setStatus('error');
        setError(e instanceof Error ? e.message : String(e));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  async function onEnablePush(): Promise<void> {
    setError(null);
    if (!('Notification' in window) || !('serviceWorker' in navigator)) {
      setError('This browser does not support web push.');
      return;
    }
    try {
      const permission = await Notification.requestPermission();
      if (permission !== 'granted') {
        setError('Push permission denied.');
        return;
      }
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.subscribe({ userVisibleOnly: true });
      // Serialize to POJO so it survives JSON round-trip.
      const asJson = sub.toJSON();
      setPushSub(asJson);
      setPushOn(true);
    } catch (e) {
      setError(
        `Push subscribe failed: ${e instanceof Error ? e.message : String(e)}`,
      );
    }
  }

  async function onSave(): Promise<void> {
    // Pre-flight: block obviously-bad SMS values so the parent gets a
    // fast local error instead of a server 400 round-trip.
    const trimmedSms = smsNumber.trim();
    if (smsOn && trimmedSms !== '' && !isUsE164(trimmedSms)) {
      setStatus('error');
      setError(
        'SMS number must be +1 followed by 10 digits — US-only for now.',
      );
      return;
    }
    setStatus('saving');
    setError(null);
    try {
      await saveNotificationPrefs({
        sms_number: trimmedSms === '' ? null : trimmedSms,
        // Email address is server-controlled (always the account email);
        // send null so we don't accidentally overwrite anything.
        email_address: null,
        push_subscription: pushSub,
        sms_enabled: smsOn,
        email_enabled: emailOn,
        push_enabled: pushOn,
      });
      setStatus('ok');
    } catch (e) {
      setStatus('error');
      setError(e instanceof Error ? e.message : String(e));
    }
  }

  async function onSendCode(): Promise<void> {
    const trimmed = smsNumber.trim();
    if (!isUsE164(trimmed)) {
      setVerifyStatus('error');
      setVerifyError('Enter a US number as +1 followed by 10 digits.');
      return;
    }
    setVerifyStatus('starting');
    setVerifyError(null);
    try {
      const r = await verifySmsStart(trimmed);
      setSmsExpiresAt(r.expires_at);
      setVerifyStatus('idle');
    } catch (e) {
      setVerifyStatus('error');
      setVerifyError(e instanceof Error ? e.message : String(e));
    }
  }

  async function onConfirmCode(): Promise<void> {
    const trimmed = smsCode.trim();
    if (!/^\d{6}$/.test(trimmed)) {
      setVerifyStatus('error');
      setVerifyError('Enter the 6-digit code from your SMS.');
      return;
    }
    setVerifyStatus('confirming');
    setVerifyError(null);
    try {
      await verifySmsConfirm(trimmed);
      // Reload prefs so we pick up the fresh sms_verified=true state
      // rather than divining it locally.
      const p = await getNotificationPrefs();
      setServerVerifiedNumber(
        p.sms_verified && p.sms_number !== null ? p.sms_number : null,
      );
      setSmsExpiresAt(p.sms_verification_expires_at);
      setSmsCode('');
      setVerifyStatus('idle');
    } catch (e) {
      setVerifyStatus('error');
      setVerifyError(e instanceof Error ? e.message : String(e));
    }
  }

  async function onTest(): Promise<void> {
    setTestStatus('sending');
    setTestResult(null);
    setError(null);
    // Persist the current toggle/number state before firing the test.
    // Otherwise the server dispatches against whatever's in the DB —
    // which after verification is `sms_enabled=false` until the parent
    // clicks Save. Saving here removes that footgun without asking
    // the parent to do it explicitly.
    const trimmedSms = smsNumber.trim();
    if (smsOn && trimmedSms !== '' && !isUsE164(trimmedSms)) {
      setTestStatus('error');
      setError(
        'SMS number must be +1 followed by 10 digits — US-only for now.',
      );
      return;
    }
    try {
      await saveNotificationPrefs({
        sms_number: trimmedSms === '' ? null : trimmedSms,
        email_address: null,
        push_subscription: pushSub,
        sms_enabled: smsOn,
        email_enabled: emailOn,
        push_enabled: pushOn,
      });
    } catch (e) {
      setTestStatus('error');
      setError(
        `Couldn't save your channel prefs before testing: ${
          e instanceof Error ? e.message : String(e)
        }`,
      );
      return;
    }
    try {
      const r = await sendTestNotification();
      setTestStatus('ok');
      setTestResult(
        r.enqueued.length === 0
          ? 'No channels enabled.'
          : `Sent test to: ${r.enqueued.join(', ')}. Check your inbox.`,
      );
      if (r.enqueued.length > 0) markOnboardingTestSent();
    } catch (e) {
      setTestStatus('error');
      setError(e instanceof Error ? e.message : String(e));
    }
  }

  if (status === 'loading') {
    return (
      <section class="space-y-3">
        <h2 class="text-2xl font-semibold">Notifications</h2>
        <p class="text-sm text-surface-muted">Loading…</p>
      </section>
    );
  }

  // Derived flag: the number in the input matches the number the
  // server has verified. Editing the input drops this to false — the
  // ✓ badge disappears, the checkbox disables, and the "Send code"
  // button reappears. Recomputed every render so it's always fresh.
  const trimmedSmsForRender = smsNumber.trim();
  const isCurrentNumberVerified =
    serverVerifiedNumber !== null &&
    trimmedSmsForRender === serverVerifiedNumber;

  return (
    <section class="space-y-3">
      <h2 class="text-2xl font-semibold">Notifications</h2>
      <Card class="space-y-4">
        <p class="text-sm text-surface-muted">
          Where should Backstop reach you when something's flagged?
        </p>

        <div class="space-y-2">
          <label class="flex items-center gap-2">
            <input
              type="checkbox"
              checked={emailOn}
              onChange={(e) =>
                setEmailOn((e.target as HTMLInputElement).checked)
              }
            />
            <span class="font-medium">Email</span>
          </label>
          <div class="rounded border border-line bg-surface-2 px-3 py-2 text-sm">
            {email !== '' ? email : 'Loading…'}
          </div>
          <p class="text-xs text-surface-muted">
            Alerts go to the email on your account. Change it under Account.
          </p>
        </div>

        <div class="space-y-2">
          <label class="flex items-center gap-2">
            <input
              type="checkbox"
              checked={smsOn}
              disabled={!isCurrentNumberVerified}
              onChange={(e) => setSmsOn((e.target as HTMLInputElement).checked)}
            />
            <span class="font-medium">SMS</span>
            {isCurrentNumberVerified ? (
              <span
                class="rounded bg-emerald-100 px-1.5 py-0.5 text-[10px] font-mono uppercase tracking-wide text-emerald-700"
                aria-label="Phone number verified"
              >
                Verified
              </span>
            ) : null}
          </label>
          <Input
            type="tel"
            placeholder="+15551234567"
            value={smsNumber}
            disabled={smsExpiresAt !== null}
            onInput={(e) => setSmsNumber((e.target as HTMLInputElement).value)}
            aria-label="Phone number"
          />
          <p class="text-xs text-surface-muted">
            US numbers only. <code>+1</code> followed by 10 digits.
          </p>
          {smsNumber.trim() !== '' && !isUsE164(smsNumber.trim()) ? (
            <p class="text-xs text-red-600">
              Enter a US number as <code>+1</code> and 10 digits, no spaces or
              dashes.
            </p>
          ) : null}

          {/* Three states: unverified (send code), code in flight, verified. */}
          {smsExpiresAt !== null ? (
            <div class="space-y-2 rounded border border-line bg-surface-2 p-3">
              <p class="text-sm">
                We texted a 6-digit code to <code>{smsNumber}</code>. Enter it
                to verify your phone.
              </p>
              <div class="flex items-center gap-2">
                <Input
                  type="text"
                  inputMode="numeric"
                  maxLength={6}
                  placeholder="123456"
                  value={smsCode}
                  onInput={(e) =>
                    setSmsCode((e.target as HTMLInputElement).value)
                  }
                  aria-label="Verification code"
                  class="max-w-[8rem]"
                />
                <Button
                  variant="primary"
                  size="sm"
                  onClick={() => void onConfirmCode()}
                  disabled={verifyStatus === 'confirming'}
                >
                  {verifyStatus === 'confirming' ? 'Verifying…' : 'Verify'}
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => void onSendCode()}
                  disabled={verifyStatus === 'starting'}
                >
                  Resend
                </Button>
              </div>
              <p class="text-xs text-surface-muted">
                Code expires in about 10 minutes. Wrong five times and you'll
                need to start over.
              </p>
            </div>
          ) : !isCurrentNumberVerified && isUsE164(smsNumber.trim()) ? (
            <Button
              variant="secondary"
              size="sm"
              onClick={() => void onSendCode()}
              disabled={verifyStatus === 'starting'}
            >
              {verifyStatus === 'starting'
                ? 'Sending…'
                : 'Send verification code'}
            </Button>
          ) : null}
          {verifyError !== null ? (
            <p class="text-xs text-red-600">{verifyError}</p>
          ) : null}
        </div>

        <div class="space-y-2">
          <label class="flex items-center gap-2">
            <input
              type="checkbox"
              checked={pushOn}
              disabled={pushSub === null}
              onChange={(e) =>
                setPushOn((e.target as HTMLInputElement).checked)
              }
            />
            <span class="font-medium">Browser push</span>
          </label>
          {pushSub === null ? (
            <Button variant="secondary" onClick={() => void onEnablePush()}>
              Enable browser push
            </Button>
          ) : (
            <p class="text-xs text-surface-muted">
              Subscribed. Push alerts will arrive as browser notifications on
              this device.
            </p>
          )}
        </div>

        <div class="flex items-center gap-3 pt-2">
          <Button onClick={() => void onSave()} disabled={status === 'saving'}>
            {status === 'saving' ? 'Saving…' : 'Save'}
          </Button>
          <Button
            variant="secondary"
            onClick={() => void onTest()}
            disabled={
              testStatus === 'sending' || (!smsOn && !emailOn && !pushOn)
            }
          >
            {testStatus === 'sending' ? 'Sending…' : 'Send test'}
          </Button>
          {status === 'ok' && testStatus !== 'ok' ? (
            <span class="text-sm text-emerald-600">Saved.</span>
          ) : null}
          {testStatus === 'ok' && testResult !== null ? (
            <span class="text-sm text-emerald-600">{testResult}</span>
          ) : null}
        </div>
        {error !== null ? <p class="text-sm text-red-600">{error}</p> : null}
      </Card>
    </section>
  );
}

function AccountPanel() {
  const s = authSession.value;
  const location = useLocation();

  async function onSignOut(): Promise<void> {
    await logout();
    clearAuthSession();
    location.route('/login', true);
  }

  return (
    <section class="space-y-3">
      <h2 class="text-2xl font-semibold">Account</h2>
      <Card class="space-y-2 text-sm">
        {s !== null ? (
          <>
            <div>
              <span class="text-surface-muted">Email:</span>{' '}
              <span class="font-medium">{s.email}</span>
            </div>
            <div>
              <span class="text-surface-muted">Parent ID:</span>{' '}
              <span class="font-mono">{s.parentId}</span>
            </div>
            <div>
              <span class="text-surface-muted">Family ID:</span>{' '}
              <span class="font-mono">{s.familyId}</span>
            </div>
          </>
        ) : (
          <div class="text-surface-muted">Not signed in.</div>
        )}
        <div class="pt-2">
          <Button variant="danger" size="sm" onClick={() => void onSignOut()}>
            Sign out
          </Button>
        </div>
      </Card>
    </section>
  );
}

export function SettingsRoute() {
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        await fetchLatest();
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <main class="mx-auto max-w-2xl space-y-6 p-4">
      <h1 class="text-3xl font-semibold">Settings</h1>
      {loading ? (
        // The other panels still read the family config, so keep the gate.
        // ByokPanel no longer depends on it: it fetches its own settings
        // from the byok row and sets the fields when they arrive.
        <p class="text-sm text-surface-muted">Loading…</p>
      ) : (
        <>
          <ByokPanel />
          <NotificationsPanel />
          <BillingPanel />
          <AccountPanel />
        </>
      )}
    </main>
  );
}
